import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { productService, Product } from "../services/product.service";
import { invoiceService, Invoice } from "../services/invoice.service";
import { formatBusinessDate, formatDate, formatDateTime } from "../lib/utils";
import {
  IndianRupee,
  Pill,
  AlertTriangle,
  Clock,
  Plus,
  ArrowUpRight,
  MoreHorizontal,
  Eye,
  Package,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { showToast } from "../lib/toast";

interface DashboardStats {
  totalProducts: number;
  activeProducts: number;
  lowStockCount: number;
  expiringCount: number;
}

interface InvoiceStats {
  todaySales: number;
  todayInvoiceCount: number;
  totalInvoices: number;
  pendingPaymentAmount: number;
  pendingPaymentCount: number;
}

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    activeProducts: 0,
    lowStockCount: 0,
    expiringCount: 0,
  });
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats>({
    todaySales: 0,
    todayInvoiceCount: 0,
    totalInvoices: 0,
    pendingPaymentAmount: 0,
    pendingPaymentCount: 0,
  });

  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [lowStockItems, setLowStockItems] = useState<Product[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [selectedStoreId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const storeId = selectedStoreId || user?.storeId;

      const [productStats, invStats, recentInv, dailySales] = await Promise.all(
        [
          productService.getProductStats(),
          invoiceService.getInvoiceStats(storeId),
          invoiceService.getInvoices({ page: 1, limit: 5, storeId }),
          invoiceService.getDailySales(7, storeId),
        ],
      );

      setStats(productStats);
      setInvoiceStats(invStats);
      setRecentInvoices(recentInv.data);

      // Fetch low stock items separately
      // Note: Ideally backend should have a dedicated endpoint for this filter
      try {
        const allProducts = await productService.getProducts({ limit: 100 });
        const criticalStock = allProducts.data
          .filter(
            (p: Product) => (p.totalStock || 0) <= (p.minStockLevel || 10),
          )
          .slice(0, 4);
        setLowStockItems(criticalStock);
      } catch (e) {
        console.error("Failed to fetch products for low stock check", e);
      }

      // Use real daily sales data from API
      const chartFromApi = dailySales.map(
        (day: { date: string; total: number }) => {
          const d = new Date(day.date);
          return {
            name: d.toLocaleDateString("en-US", { weekday: "short" }),
            value: day.total,
          };
        },
      );
      setChartData(chartFromApi);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportRecentTransactions = () => {
    if (recentInvoices.length === 0) {
      showToast.error("There are no recent transactions to export");
      return;
    }
    const csvCell = (value: unknown) => {
      let text = String(value ?? "");
      if (/^[=+\-@]/.test(text)) text = `'${text}`;
      return `"${text.replace(/"/g, '""')}"`;
    };
    const rows = recentInvoices.map((invoice) => [
      invoice.invoiceNumber,
      invoice.customer?.name || "Walk-in Customer",
      formatDateTime(invoice.invoiceDate),
      Number(invoice.totalAmount).toFixed(2),
      invoice.paymentStatus,
    ]);
    const csv = [
      ["Invoice ID", "Customer", "Date", "Amount (INR)", "Status"],
      ...rows,
    ]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(
      new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `recent-transactions-${formatBusinessDate(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showToast.success("Recent transactions exported");
  };

  return (
    <div className="space-y-8 font-sans max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Dashboard Overview
          </h1>
          <p className="mt-1 text-gray-500">
            Welcome back, here's what's happening at your pharmacy today.
          </p>
        </div>
        <Link
          to="/pos"
          className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-5 py-2.5 rounded-xl font-semibold transition-all shadow-sm shadow-teal-200"
        >
          <Plus className="w-5 h-5" />
          <span>New Invoice</span>
        </Link>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1: Today's Sales */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-start group hover:shadow-md transition-all">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              TODAY'S SALES
            </p>
            <h3 className="text-3xl font-bold text-slate-900 mb-2">
              {loading ? "..." : `₹${invoiceStats.todaySales.toLocaleString()}`}
            </h3>
            <p className="text-sm font-medium text-emerald-600 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span className="text-gray-400 font-normal ml-1">
                Today's Revenue
              </span>
            </p>
          </div>
          <div className="p-3 bg-teal-50 text-teal-600 rounded-xl">
            <IndianRupee className="w-6 h-6" />
          </div>
        </div>

        {/* Card 2: Total Products */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-start group hover:shadow-md transition-all">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              TOTAL PRODUCTS
            </p>
            <h3 className="text-3xl font-bold text-slate-900 mb-2">
              {loading ? "..." : stats.totalProducts}
            </h3>
            <p className="text-sm font-medium text-emerald-600 flex items-center">
              <span className="text-gray-400 font-normal">
                {stats.activeProducts} Active items
              </span>
            </p>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <Pill className="w-6 h-6" />
          </div>
        </div>

        {/* Card 3: Low Stock */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-start group hover:shadow-md transition-all">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              LOW STOCK ALERTS
            </p>
            <h3 className="text-3xl font-bold text-slate-900 mb-2">
              {loading ? "..." : stats.lowStockCount}{" "}
              <span className="text-lg font-normal text-gray-500">Items</span>
            </h3>
            {stats.lowStockCount > 0 && (
              <div className="inline-flex items-center px-2 py-1 bg-red-50 text-red-600 text-xs font-bold rounded mt-1">
                Needs immediate attention
              </div>
            )}
          </div>
          <div className="p-3 bg-red-50 text-red-500 rounded-xl">
            <AlertTriangle className="w-6 h-6" />
          </div>
        </div>

        {/* Card 4: Pending Payments */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-start group hover:shadow-md transition-all">
          <div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              PENDING PAYMENTS
            </p>
            <h3 className="text-3xl font-bold text-slate-900 mb-2">
              {loading
                ? "..."
                : `₹${invoiceStats.pendingPaymentAmount.toLocaleString()}`}
            </h3>
            <p className="text-sm font-medium text-gray-400 flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              From {invoiceStats.pendingPaymentCount} invoices
            </p>
          </div>
          <div className="p-3 bg-orange-50 text-orange-500 rounded-xl">
            <IndianRupee className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Main Grid: Chart & Right Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Chart */}
        <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-bold text-gray-900">
              Sales & Revenue Trends
            </h3>
            <select className="bg-gray-50 border-none text-sm text-gray-600 font-medium py-2 px-4 rounded-lg outline-none cursor-pointer hover:bg-gray-100 transition-colors">
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
            </select>
          </div>

          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#14B8A6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#E2E8F0"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#94A3B8", fontSize: 13 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#94A3B8", fontSize: 13 }}
                  tickFormatter={(value: number) => `₹${value}`}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#14B8A6"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorValue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Column: Critical Low Stock */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 h-full">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              Critical Low Stock
            </h3>
            <Link
              to="/reorder"
              className="text-sm font-semibold text-teal-600 hover:text-teal-700"
            >
              View All
            </Link>
          </div>

          <div className="space-y-4">
            {lowStockItems.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No critical stock alerts</p>
              </div>
            ) : (
              lowStockItems.map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-red-50 rounded-xl border border-red-100 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg text-red-500 shadow-sm">
                      <Pill className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">
                        {item.name}
                      </h4>
                      <p className="text-red-500 text-xs font-semibold">
                        Only {item.totalStock} {item.unit} left
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/reorder"
                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    Order
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-xl font-bold text-gray-900">
            Recent Transactions
          </h3>

          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Filter transactions..."
              className="bg-gray-50 border border-gray-200 text-sm rounded-lg px-4 py-2 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 w-full sm:w-64"
            />
            <button className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50">
              Filter
            </button>
            <button
              type="button"
              onClick={exportRecentTransactions}
              className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              Export
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Invoice ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {recentInvoices.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-400"
                  >
                    <div className="flex flex-col items-center">
                      <MoreHorizontal className="w-8 h-8 opacity-20 mb-2" />
                      No recent transactions found
                    </div>
                  </td>
                </tr>
              ) : (
                recentInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="group hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-semibold text-teal-600">
                      #{inv.invoiceNumber}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                          {inv.customer?.name?.[0] || "W"}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {inv.customer?.name || "Walk-in Customer"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">
                      {formatDate(inv.invoiceDate)}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-900">
                      ₹{Number(inv.totalAmount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          inv.paymentStatus === "PAID"
                            ? "bg-green-100 text-green-700"
                            : inv.paymentStatus === "PARTIAL"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                        }`}
                      >
                        {inv.paymentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="text-gray-400 hover:text-teal-600 transition-colors inline-block"
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
